import React from 'react';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';

import { isValidBrandDomainKey } from '../../helpers/formatting.js';

export const FIELD_SECTIONS = [
  { title: 'Brand Details', fieldNames: ['name', 'domain', 'altDomains', 'username', 'code'] },
  { title: 'Billing', fieldNames: ['stripeCustomerId', 'stripeSubscriptionScheduleId'] },
  { title: 'Network & Rates', fieldNames: ['force_merchant', 'isClosedProgram', 'commission_rate', 'commission_rate_returning', 'network_rate', 'network_rate_gmv_cut', 'returnWindowLength', 'cookieWindow'] }, // prettier-ignore
  { title: 'Internal Notifications', fieldNames: ['NotificationsUser_id'] },
  { title: 'Onboarding Overrides', fieldNames: ['canSkipShopifyApp', 'canSkipOrderUpdates', 'canSkipOrderCancellations'], collapsible: true },
  { title: 'Profile & Images', fieldNames: ['description', 'logo', 'mobileCoverImage', 'mobileBadgeImage'], collapsible: true }
];

export const HALF_WIDTH_FIELDS = new Set([
  'username',
  'code',
  'commission_rate',
  'commission_rate_returning',
  'network_rate',
  'network_rate_gmv_cut',
  'returnWindowLength',
  'cookieWindow'
]);

export const NETWORK_LABELS = {
  shopmyshelf: 'ShopMy',
  rakuten: 'Rakuten',
  awin: 'Awin',
  cj: 'CJ',
  impact: 'Impact',
  goaffpro: 'GoAffPro',
  manual: 'Manual',
  webgains: 'Webgains',
  refersion: 'Refersion',
  ebay: 'eBay',
  flexoffer: 'FlexOffers',
  cf: 'cf',
  digidip: 'digidip',
  amazon: 'Amazon',
  skimlink: 'Skimlinks',
  tradedoubler: 'Tradedoubler',
  avantlink: 'AvantLink',
  partnerize: 'Partnerize',
  pepperjam: 'Pepperjam'
};

export const CLEARABLE_FIELDS = new Set(['NotificationsUser_id', 'commission_rate_returning']);
export const PERCENT_FIELDS = new Set(['commission_rate', 'commission_rate_returning', 'network_rate', 'network_rate_gmv_cut']);
export const DAY_FIELDS = new Set(['returnWindowLength', 'cookieWindow']);
export const COPYABLE_READONLY_FIELDS = new Set(['stripeCustomerId', 'stripeSubscriptionScheduleId', 'logo', 'mobileCoverImage', 'mobileBadgeImage']);
export const URL_READONLY_FIELDS = new Set(['logo', 'mobileCoverImage', 'mobileBadgeImage']);

// Checking Closed Program forces these values and renders the fields read-only until unchecked.
export const CLOSED_PROGRAM_FORCED_VALUES = { force_merchant: 'shopmyshelf', commission_rate: 0, commission_rate_returning: '' };

// Brand details/billing/profile are consumed at registerBrand; network & rates at startBrandSubscription.
// The editable set shrinks accordingly — only the notifications recipient stays live forever.
export const EDITABLE_DURING_ONBOARDING = new Set([
  'force_merchant',
  'isClosedProgram',
  'commission_rate',
  'commission_rate_returning',
  'network_rate',
  'network_rate_gmv_cut',
  'returnWindowLength',
  'cookieWindow',
  'canSkipShopifyApp',
  'canSkipOrderUpdates',
  'canSkipOrderCancellations',
  'NotificationsUser_id'
]);
export const EDITABLE_AFTER_LIVE = new Set(['NotificationsUser_id']);

export const STALLED_NOT_STARTED_DAYS = 7;
export const STALLED_IN_PROGRESS_DAYS = 14;

export const FIELD_DEFINITIONS = [
  {
    name: 'name',
    label: 'Brand Name*',
    tooltip: "The brand's name as it should appear across the platform. Should match what the brand uses publicly.",
    type: 'text',
    value: '',
    isFieldValid: value => value?.length >= 2,
    isValid: true,
    errorMessage: 'Please enter a valid brand name'
  },
  {
    name: 'domain',
    label: 'Domain*',
    tooltip: 'Primary website domain (e.g. brand.com). Used to attribute affiliate clicks and to link the brand to its merchant network records.',
    type: 'text',
    value: '',
    isFieldValid: value => isValidBrandDomainKey(value),
    isValid: true,
    errorMessage: 'Please enter a valid domain'
  },
  {
    name: 'altDomains',
    label: 'Alt Domains (comma separated)',
    tooltip:
      'Comma-separated alternate domains (e.g. brand.co, shop.brand.com). Treated as equivalent to the primary domain for click attribution and merchant linking.',
    type: 'text',
    value: '',
    isFieldValid: value => value === '' || value.split(',').every(d => isValidBrandDomainKey(d.trim())),
    isValid: true,
    errorMessage: 'Please enter valid domains separated by commas'
  },
  {
    name: 'username',
    label: 'Username*',
    tooltip:
      'Login username for the brand account. Must be unique across all users. Lowercase letters and numbers only — no spaces, capitals, dashes, underscores, or special characters.',
    type: 'text',
    value: '',
    sanitize: value => value.toLowerCase().replace(/[^a-z0-9]/g, ''),
    isFieldValid: value => value?.length >= 2 && /^[a-z0-9]+$/.test(value),
    isValid: true,
    errorMessage: 'Username must be at least 2 characters and contain only lowercase letters and numbers'
  },
  {
    name: 'code',
    label: 'Registration Code*',
    tooltip:
      "The code the brand enters during signup to claim this account. Send this to the brand once it's created. Lowercase letters, numbers, dashes, and underscores only — no spaces, capitals, or special characters.",
    type: 'text',
    value: '',
    sanitize: value => value.toLowerCase().replace(/[^a-z0-9_-]/g, ''),
    isFieldValid: value => value?.length >= 2 && /^[a-z0-9_-]+$/.test(value),
    isValid: true,
    errorMessage: 'Registration code must be at least 2 characters and contain only lowercase letters, numbers, dashes, or underscores'
  },
  {
    name: 'stripeCustomerId',
    label: 'Stripe Customer ID*',
    tooltip: 'Stripe Customer ID for billing this brand. Required for the subscription to activate and for the Stripe webhook to find the brand.',
    type: 'text',
    value: '',
    isFieldValid: value => value?.length >= 2,
    isValid: true,
    errorMessage: 'Please enter the Stripe Customer ID'
  },
  {
    name: 'stripeSubscriptionScheduleId',
    label: 'Stripe Subscription Schedule ID*',
    tooltip: "Stripe Subscription Schedule ID that controls when the brand's subscription activates after payment setup.",
    type: 'text',
    value: '',
    isFieldValid: value => value?.length >= 2,
    isValid: true,
    errorMessage: 'Please enter the Stripe Subscription Schedule ID'
  },
  {
    name: 'description',
    label: 'Description',
    tooltip: 'Short brand bio shown on brand profile pages. Visible to talent and used during the brand onboarding flow.',
    type: 'textarea',
    value: '',
    isFieldValid: () => true,
    isValid: true,
    customComponent: (field, index, onFieldChange) => {
      return (
        <div className='brand-onboarding-result-input-container'>
          <textarea
            className={cn('brand-onboarding-result-input-container-input', { invalid: !field.isValid, filled: !!field.value })}
            value={field.value}
            onChange={e => {
              const { value } = e.target;
              onFieldChange(index, { value });
            }}
            placeholder='Answer here...'
            rows={3}
          />
        </div>
      );
    }
  },
  {
    name: 'logo',
    label: 'Logo URL',
    tooltip: 'Brand logo URL (PNG or JPG). Shown in product feeds, affiliate network listings, and outbound emails.',
    type: 'text',
    value: '',
    isFieldValid: () => true,
    isValid: true,
    errorMessage: ''
  },
  {
    name: 'mobileCoverImage',
    label: 'Mobile Cover Image URL',
    tooltip: "Header image shown on the brand's card in the mobile app when talent browses brands.",
    type: 'text',
    value: '',
    isFieldValid: () => true,
    isValid: true,
    errorMessage: ''
  },
  {
    name: 'mobileBadgeImage',
    label: 'Mobile Badge Image URL',
    tooltip: "Badge/icon shown alongside the brand's mobile cover image. Smaller mark, often a square version of the logo.",
    type: 'text',
    value: '',
    isFieldValid: () => true,
    isValid: true,
    errorMessage: ''
  },
  {
    name: 'returnWindowLength',
    label: 'Days Before Orders Lock',
    tooltip:
      'Days after an order is placed before its affiliate commission locks. Cancellations within this window void the commission; after it, payouts are final.',
    type: 'number',
    value: 30,
    defaultValue: 30,
    isFieldValid: value => value === '' || Number.isInteger(Number(value)),
    isValid: true,
    errorMessage: 'Days Before Orders Lock must be a whole number'
  },
  {
    name: 'cookieWindow',
    label: 'Cookie Window (days)',
    tooltip:
      "How long the affiliate click cookie persists in a customer's browser. A purchase within this window after a click still attributes to the talent.",
    type: 'number',
    value: 30,
    defaultValue: 30,
    isFieldValid: value => value === '' || Number.isInteger(Number(value)),
    isValid: true,
    errorMessage: 'Cookie Window must be a whole number of days'
  },
  {
    name: 'commission_rate',
    label: 'Commission Rate (%)',
    tooltip:
      'Base commission percent the brand pays on each attributed affiliate order. Applies to all customers unless the returning-customer rate is set.',
    type: 'number',
    value: 15,
    defaultValue: 15,
    isFieldValid: value => value === '' || Number.isInteger(Number(value)),
    isValid: true,
    errorMessage: 'Commission Rate must be a whole-number percent'
  },
  {
    name: 'commission_rate_returning',
    label: 'Returning Customer Rate (%)',
    tooltip:
      'Optional separate commission percent applied when the customer has purchased from this brand before. Leave blank to use the base commission rate for everyone.',
    type: 'number',
    value: '',
    placeholder: 'Leave blank to use the base rate',
    isFieldValid: value => value === '' || Number.isInteger(Number(value)),
    isValid: true,
    errorMessage: 'Returning Customer Rate must be a whole-number percent'
  },
  {
    name: 'network_rate',
    label: 'Network Rate (%)',
    tooltip: "Percent ShopMy takes from the brand's commission as a network fee. If 0, the GMV-cut model below applies instead.",
    type: 'number',
    value: 0,
    defaultValue: 0,
    isFieldValid: value => value === '' || Number.isInteger(Number(value)),
    isValid: true,
    errorMessage: 'Network Rate must be a whole-number percent'
  },
  {
    name: 'network_rate_gmv_cut',
    label: 'Network Rate GMV Cut (%)',
    tooltip:
      "Percent of total order GMV ShopMy takes as a network fee. Tied to the brand's subscription plan: 2.9% for Core, 3.9% for Affiliates. Only applies when Network Rate above is 0.",
    type: 'number',
    value: 2.9,
    defaultValue: 2.9,
    isFieldValid: () => true,
    isValid: true,
    errorMessage: ''
  },
  {
    name: 'force_merchant',
    label: 'Affiliate Network*',
    tooltip: 'Locks the brand to a specific affiliate network after onboarding (stored as force_merchant).',
    type: '',
    value: '',
    isFieldValid: value => !!value,
    isValid: true,
    errorMessage: 'Select an affiliate network',
    customComponent: (field, index, onFieldChange) => {
      return (
        <div className='brand-onboarding-result-input-container'>
          <div className='brand-onboarding-input-icon'>
            <FontAwesomeIcon icon={faAngleDown} />
          </div>

          <select
            className={cn('brand-onboarding-result-input-container-input', { invalid: !field.isValid })}
            value={field.value}
            onChange={e => {
              const { value } = e.target;
              onFieldChange(index, { value });
            }}
          >
            <option value=''>Select a network...</option>
            {Object.entries(NETWORK_LABELS).map(([networkKey, networkLabel]) => (
              <option key={networkKey} value={networkKey}>
                {networkLabel}
              </option>
            ))}
          </select>
        </div>
      );
    }
  },
  {
    name: 'isClosedProgram',
    label: 'Closed Program',
    tooltip:
      'Locks the brand to the ShopMy network at a 0% base commission — only creators the brand gives a custom rate can earn. Also lets the brand skip tracking setup during onboarding.',
    type: 'checkbox',
    value: false,
    isFieldValid: () => true,
    isValid: true,
    errorMessage: ''
  },
  {
    name: 'canSkipShopifyApp',
    label: 'Can Skip Shopify App Connection',
    tooltip: 'If checked, the brand can finish onboarding without connecting the ShopMy Shopify app.',
    type: 'checkbox',
    value: false,
    isFieldValid: () => true,
    isValid: true,
    errorMessage: ''
  },
  {
    name: 'canSkipOrderUpdates',
    label: 'Can Skip Order Updates',
    tooltip: 'If checked, the brand can finish onboarding without configuring order-update tracking.',
    type: 'checkbox',
    value: false,
    isFieldValid: () => true,
    isValid: true,
    errorMessage: ''
  },
  {
    name: 'canSkipOrderCancellations',
    label: 'Can Skip Order Cancellations',
    tooltip: 'If checked, the brand can finish onboarding without configuring order-cancellation tracking.',
    type: 'checkbox',
    value: false,
    isFieldValid: () => true,
    isValid: true,
    errorMessage: ''
  },
  {
    name: 'NotificationsUser_id',
    label: 'Onboarding Updates Recipient',
    tooltip:
      "Team member whose personal Slack channel receives this brand's onboarding updates, in addition to #brand-onboarding-updates. Defaults to whoever creates the code; the recipient needs a personal Slack channel configured to receive them.",
    type: '',
    value: '',
    options: [],
    isFieldValid: () => true,
    isValid: true,
    errorMessage: '',
    customComponent: (field, index, onFieldChange) => {
      return (
        <div className='brand-onboarding-result-input-container'>
          <div className='brand-onboarding-input-icon'>
            <FontAwesomeIcon icon={faAngleDown} />
          </div>

          <select
            className={cn('brand-onboarding-result-input-container-input', { invalid: !field.isValid })}
            value={field.value}
            onChange={e => {
              const { value } = e.target;
              onFieldChange(index, { value: parseInt(value) || '' });
            }}
          >
            <option value=''>{field.emptyOptionLabel || 'Code creator (default)'}</option>
            {!!field.value && !field.options.some(admin => admin.id === field.value) && (
              <option value={field.value}>{field.currentRecipientLabel || `User ${field.value}`}</option>
            )}
            {field.options.map(admin => (
              <option key={admin.id} value={admin.id}>
                {admin.name}
              </option>
            ))}
          </select>
        </div>
      );
    }
  }
];
